import { Box, Button, Typography } from "@mui/material";
import ModalLike from "components/modallike";
import { ReactComponent as Logo } from "assets/logo.svg";
import FormInput from "components/forminput";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { object, string } from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import config from "config";
import useUser from "hooks/useUser";
import { useGoogleLogin } from "@react-oauth/google";
import { Google } from "@mui/icons-material";

const initialValues = {
  username: "",
  password: "",
};

const validationSchema = object({
  username: string().required("Username is required"),
  password: string().min(6).required("Password is required"),
});

async function login(values: typeof initialValues) {
  const { data } = await axios.post("/auth/agent/login", values);
  return data as AuthRes;
}

function Login() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const { data, refetch } = useUser();
  const { state } = useLocation();
  const submit = useMutation(login, {
    onSuccess(data) {
      localStorage.setItem(config.tokenKey, data.token);
      client.cancelQueries([config.userKey]);
      client.setQueryData([config.userKey], data.user);
      client.refetchQueries([config.userKey]);
      navigate("/dashboard/home");
    },
  });
  const cont = useGoogleLogin({
    onSuccess: ({ code }) =>
      axios.post("/auth/agent/google", { code }).then(({ data }) => {
        localStorage.setItem(config.tokenKey, data.token);
        client.cancelQueries([config.userKey]);
        client.setQueryData([config.userKey], data.user);
        client.refetchQueries([config.userKey]);
        navigate("/dashboard/home");
        refetch();
      }),
    flow: "auth-code",
  });
  if (data) {
    return <Navigate to={state.from || "/dashboard/home"} replace />;
  }

  return (
    <div>
      <ModalLike size="large">
        <Box
          sx={{
            textAlign: "center",
          }}>
          <Logo />
          <Typography variant="h5" fontWeight="bold" my="1rem" component="h1">
            Please login to continue
          </Typography>
        </Box>
        <Formik
          initialValues={initialValues}
          onSubmit={(data) => submit.mutate(data)}
          validationSchema={validationSchema}>
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
            <Box component="form" onSubmit={handleSubmit}>
              <FormInput
                id="email"
                value={values.username}
                name="username"
                label="Email Address"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.username && !!errors.username}
                helperText={touched.username && errors.username}
              />
              <FormInput
                id="password"
                value={values.password}
                name="password"
                label="Password"
                type="password"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && !!errors.password}
                helperText={touched.password && errors.password}
              />
              <Button
                disabled={submit.isLoading}
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                sx={{
                  my: "1rem",
                  textTransform: "none",
                  fontWeight: "bold",
                }}>
                Sign in
              </Button>
              <Typography variant="body2" color="textSecondary" textAlign="center">
                or continue with
              </Typography>
              <Button
                startIcon={<Google />}
                onClick={cont}
                variant="outlined"
                color="primary"
                fullWidth
                size="large"
                sx={{
                  my: "1rem",
                  textTransform: "none",
                  fontWeight: "bold",
                }}>
                Google
              </Button>
              <Typography variant="body2" color="textSecondary" textAlign="center">
                Don't have an account?{" "}
                <Typography variant="body2" color="primary" component={Link} to="/register" fontWeight="bold">
                  Sign up
                </Typography>
              </Typography>
            </Box>
          )}
        </Formik>
      </ModalLike>
    </div>
  );
}

export default Login;
